<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import ActivityCell from '@/components/features/activities/ActivityCell.vue'
import PrismaTodoCell from '@/components/features/activities/PrismaTodoCell.vue'
import { storeToRefs } from 'pinia'
import { useActivitiesStore } from '@/stores/useActivityStore'
import { useUserStore } from '@/stores/useUserStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useToast } from 'vue-toast-notification'
import { getLongTimeFr } from '@/utils/dateUtils'

/** Prisma si VITE_SUITE_URL est défini ; forcer l’ancien Odoo uniquement avec VITE_USE_SUITE_DAILY_TODOS=false */
const suiteUrlConfigured = Boolean(import.meta.env.VITE_SUITE_URL?.trim())
const forceOdooSidebar = import.meta.env.VITE_USE_SUITE_DAILY_TODOS === 'false'
const tryPrismaSidebarFirst = suiteUrlConfigured && !forceOdooSidebar

const sidebarMode = ref<'prisma' | 'odoo'>('odoo')

const activitiesStore = useActivitiesStore()
const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const {
  loading,
  error,
  todayActivities,
  outdatedActivities,
  suiteTodosToday,
  suiteTodosOverdue,
} = storeToRefs(activitiesStore)
const $toast = useToast()

async function bootSidebar(): Promise<void> {
  if (tryPrismaSidebarFirst) {
    const ok = await activitiesStore.loadSuiteDailyTodos({ quiet: true })
    if (ok) {
      sidebarMode.value = 'prisma'
      return
    }
  }
  sidebarMode.value = 'odoo'
  if (boostCoreStore.user?.email) {
    await userStore.getUserByEmail(boostCoreStore.user?.email)
    if (userStore.userConnected?.id)
      await activitiesStore.getActivitiesByUserId(userStore.userConnected?.id)
  }
}

onMounted(() => {
  if (boostCoreStore.user) {
    void bootSidebar()
    return
  }
  const stop = watch(() => boostCoreStore.user, (u) => {
    if (!u) return
    stop()
    void bootSidebar()
  })
})

watch(error, () => {
  if (!error.value) return
  $toast.error(error.value)
})
</script>

<template>
  <div v-if="loading" class="w-full">
    <div class="skeleton w-full h-12 mt-2" />
    <div class="skeleton w-full h-12 mt-2" />
  </div>
  <div v-else-if="sidebarMode === 'prisma'" class="w-full">
    <h3>Mes taches journalières</h3>
    <div class="bg-base-100 rounded-box p-2">
      <a class="flex justify-between" v-if="suiteTodosOverdue.length > 0" href="/dashboard/todos">
        <h3 class="badge badge-warning rounded-xl p-2 mt-2 font-bold w-full">
          ⚠ {{ suiteTodosOverdue.length }} tâche{{ suiteTodosOverdue.length == 1 ? '' : 's' }} en retard
        </h3>
      </a>
      <div v-if="suiteTodosOverdue.length > 0" class="divider"></div>
      <template v-if="suiteTodosOverdue.length > 0">
        <p class="m-2 text-sm font-semibold">En retard</p>
        <prisma-todo-cell v-for="t in suiteTodosOverdue" :key="t.uuid" :todo="t" />
        <div class="divider"></div>
      </template>
      <h3 class="m-2 font-bold">{{ getLongTimeFr(new Date()) }}</h3>
      <p v-if="suiteTodosToday.length === 0" class="m-2 text-sm opacity-70">
        Aucune tâche en attente pour aujourd’hui (échéance ce jour, ou créée aujourd’hui sans date d’échéance).
      </p>
      <prisma-todo-cell
        v-for="t in suiteTodosToday"
        :key="t.uuid"
        :todo="t"
      />
    </div>
  </div>
  <div v-else class="w-full">
    <h3>Mes taches journalières</h3>
    <div class="bg-base-100 rounded-box p-2">
      <a class="flex justify-between" v-if="outdatedActivities.length > 0" href="/activities">
        <h3 class="badge badge-warning rounded-xl p-2 mt-2 font-bold w-full">
          ⚠ {{ outdatedActivities.length }} To Do{{
            outdatedActivities.length == 1 ? '' : 's'
          }}
          dépassé{{ outdatedActivities.length == 1 ? '' : 's' }}
        </h3>
      </a>
      <div v-if="outdatedActivities.length > 0" class="divider"></div>
      <h3 class="m-2 font-bold">{{ getLongTimeFr(new Date()) }}</h3>
      <p v-if="todayActivities.length === 0" class="m-2 text-sm opacity-70">
        Aucune tâche « appel partenaire » prévue ce jour (mail.activity / phonecall sur contact).
      </p>
      <activity-cell
        :activity="activity"
        v-for="activity in todayActivities"
        :key="activity.id"
      ></activity-cell>
    </div>
  </div>
</template>
